import { gql } from '@apollo/client';
import React from 'react';
import loadable from '@loadable/component';
import { ModuleSectionFragment } from '../generated/types/ModuleSectionFragment';
import { DIVIDER_PUMO_MODULE_FRAGMENT } from './DividerPumoModule';
import { SINGLE_PUMO_MODULE_FRAGMENT } from './SinglePumoModule';
import { MULTI_PUMO_MODULE_FRAGMENT } from './MultiPumoModule';
import { HOMEPAGE_HEADER_PUMO_MODULE_FRAGMENT } from './HomePageHeaderPumoModule';
import { CAROUSEL_PUMO_MODULE_FRAGMENT } from './CarouselPumoModule';
import { RECOMMENDATIONS_PUMO_MODULE_FRAGMENT } from './RecommendationsPumoModule';
import { EMAIL_PUMO_MODULE_FRAGMENT } from './EmailPumoModule';
import { PILLS_PUMO_MODULE_FRAGMENT } from './PillsPumoModule';
import { TRUNKSHOW_COUNTDOWN_PUMO_MODULE_FRAGMENT } from './TrunkshowCountdownPumoModule';
import { PUMO_LIVESTREAM_MODULE_FRAGMENT } from './PumoLivestreamModule';

const DividerPumoModule = loadable(() => import('./DividerPumoModule'));
const SinglePumoModule = loadable(() => import('./SinglePumoModule'));
const MultiPumoModule = loadable(() => import('./MultiPumoModule'));
const HomePageHeaderPumoModule = loadable(() => import('./HomePageHeaderPumoModule'));
const CarouselPumoModule = loadable(() => import('./CarouselPumoModule'));
const RecommendationsPumoModule = loadable(() => import('./RecommendationsPumoModule'));
const EmailPumoModule = loadable(() => import('./EmailPumoModule'));
const PillsPumoModule = loadable(() => import('./PillsPumoModule'));
const TrunkshowCountdownPumoModule = loadable(() => import('./TrunkshowCountdownPumoModule'));
const PumoLivestreamModule = loadable(() => import('./PumoLivestreamModule'));

export const MODULE_SECTION_FRAGMENT = gql`
  fragment ModuleSectionFragment on PumoModule {
    __typename
    ... on LivestreamPumoModule {
      id
    }
    ... on DividerPumoModule {
      id
    }
    ... on SinglePumoModule {
      id
    }
    ... on MultiPumoModule {
      id
    }
    ... on CarouselPumoModule {
      id
    }
    ... on RecommendationsPumoModule {
      id
    }
    ... on HomePageHeaderPumoModule {
      id
    }
    ... on EmailPumoModule {
      id
    }
    ... on PillsPumoModule {
      id
    }
    ... on TrunkshowCountdownPumoModule {
      id
    }
    ...CarouselPumoModuleFragment
    ...DividerPumoModuleFragment
    ...EmailPumoModuleFragment
    ...HomePageHeaderPumoModuleFragment
    ...MultiPumoModuleFragment
    ...PillsPumoModuleFragment
    ...RecommendationsPumoModuleFragment
    ...SinglePumoModuleFragment
    ...TrunkshowCountdownPumoModuleFragment
    ...PumoLivestreamModuleFragment
  }
  ${CAROUSEL_PUMO_MODULE_FRAGMENT}
  ${DIVIDER_PUMO_MODULE_FRAGMENT}
  ${EMAIL_PUMO_MODULE_FRAGMENT}
  ${HOMEPAGE_HEADER_PUMO_MODULE_FRAGMENT}
  ${MULTI_PUMO_MODULE_FRAGMENT}
  ${PILLS_PUMO_MODULE_FRAGMENT}
  ${RECOMMENDATIONS_PUMO_MODULE_FRAGMENT}
  ${SINGLE_PUMO_MODULE_FRAGMENT}
  ${TRUNKSHOW_COUNTDOWN_PUMO_MODULE_FRAGMENT}
  ${PUMO_LIVESTREAM_MODULE_FRAGMENT}
`;

export interface Props {
  modules: ModuleSectionFragment[];
}

export const ModuleSection: React.FC<Props> = ({ modules }) => {
  return (
    <section className="PumoModule">
      {modules.map(moduleSection => {
        const key = [moduleSection.__typename, moduleSection.id].join(':');

        switch (moduleSection.__typename) {
          case 'DividerPumoModule':
            return <DividerPumoModule key={key} dividerPumoModule={moduleSection} />;
          case 'SinglePumoModule':
            return <SinglePumoModule key={key} singlePumoModule={moduleSection} />;
          case 'MultiPumoModule':
            return <MultiPumoModule key={key} multiPumoModule={moduleSection} />;
          case 'HomePageHeaderPumoModule':
            return <HomePageHeaderPumoModule key={key} homePageHeaderPumoModule={moduleSection} />;
          case 'CarouselPumoModule':
            return <CarouselPumoModule key={key} carouselPumoModule={moduleSection} />;
          case 'RecommendationsPumoModule':
            return (
              <RecommendationsPumoModule key={key} recommendationsPumoModule={moduleSection} />
            );
          case 'EmailPumoModule':
            return <EmailPumoModule key={key} emailPumoModule={moduleSection} />;
          case 'PillsPumoModule':
            return <PillsPumoModule key={key} pillsPumoModule={moduleSection} />;
          case 'TrunkshowCountdownPumoModule':
            return (
              <TrunkshowCountdownPumoModule
                key={key}
                trunkshowCountdownPumoModule={moduleSection}
              />
            );
          case 'LivestreamPumoModule':
            return <PumoLivestreamModule key={key} livestream={moduleSection} />;
        }
      })}
    </section>
  );
};
